'use client'

import type React from 'react'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { User, Globe, CheckCircle } from 'lucide-react'
import type { BookingData } from '@/types/booking'

type FlowType = NonNullable<BookingData['flowType']>

interface StepChooseFlowProps {
  onSelect: (f: FlowType) => void
  selectedFlow?: FlowType | null
  allowedFlows?: FlowType[]
}

const StepChooseFlow: React.FC<StepChooseFlowProps> = ({
  onSelect,
  selectedFlow,
  allowedFlows,
}) => {
  const options = [
    {
      type: 'In-person' as FlowType,
      title: 'In-Person Consultation',
      description: 'Visit our clinic for a direct consultation.',
      Icon: User,
      color: 'text-blue-600',
      selectedRing: 'border-blue-500 ring-2 ring-blue-200',
      iconBg: 'bg-blue-100',
    },
    {
      type: 'Online' as FlowType,
      title: 'Online Consultation',
      description: 'Connect with our experts from anywhere.',
      Icon: Globe,
      color: 'text-purple-600',
      selectedRing: 'border-purple-500 ring-2 ring-purple-200',
      iconBg: 'bg-purple-100',
    },
  ]

  const visibleOptions = allowedFlows?.length
    ? options.filter((option) => allowedFlows.includes(option.type))
    : options

  const gridClass =
    visibleOptions.length === 2
      ? 'grid grid-cols-1 gap-3 px-1 sm:grid-cols-2 sm:gap-4 sm:px-0 md:mx-auto md:max-w-3xl'
      : 'grid grid-cols-1 gap-3 px-1 sm:grid-cols-2 sm:gap-4 sm:px-0 md:grid-cols-3 md:gap-6'

  return (
    <div className={gridClass}>
      {visibleOptions.map(
        ({ type, title, description, Icon, color, selectedRing, iconBg }) => {
          const isSelected = selectedFlow === type
          return (
            <Card
              key={type}
              role="button"
              aria-pressed={isSelected}
              tabIndex={0}
              onClick={() => onSelect(type)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelect(type)
                }
              }}
              className={[
                'relative h-full cursor-pointer transition-all duration-300 border-2',
                'hover:-translate-y-1 hover:shadow-md active:scale-[0.98]',
                isSelected
                  ? `${selectedRing} shadow-lg`
                  : 'border-transparent hover:border-blue-400',
              ].join(' ')}
            >
              {isSelected && (
                <span className="absolute right-2 top-2 sm:right-3 sm:top-3">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600" />
                </span>
              )}
              <CardContent className="flex h-full flex-col items-center justify-center p-4 sm:p-6 text-center">
                <div
                  className={`mb-3 sm:mb-4 rounded-full p-2.5 sm:p-3 ${iconBg}`}
                >
                  <Icon
                    className={`w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 ${color}`}
                  />
                </div>
                <CardTitle className="text-base sm:text-lg md:text-xl font-semibold mb-1 sm:mb-2 text-gray-800">
                  {title}
                </CardTitle>
                <p className="text-xs sm:text-sm text-gray-600">
                  {description}
                </p>
                {isSelected && (
                  <p className="mt-2 sm:mt-4 text-xs sm:text-sm font-semibold text-gray-800">
                    Selected
                  </p>
                )}
              </CardContent>
            </Card>
          )
        },
      )}
    </div>
  )
}

export default StepChooseFlow
